import { useQuery } from "@tanstack/react-query";
import type { AreaMapResponse } from "@/features/deployment-dashboard/shared/api/area-map-response";
import {
  areaMapQueryKey,
  buildAreaMapPath,
} from "@/features/deployment-dashboard/shared/api/area-map-query";
import { useRuntimeHttpClient } from "@/features/runtime-session/hooks/use-runtime-http-client";

/**
 * Fetches a single deployment area's map from the runtime API: its coordinate
 * reference system, calibrated floor plan (with a temporary signed image URL and
 * world-to-pixel transform), and observation area boundaries in world coordinates.
 *
 * Reuses the `areas_list` operation grant, matching `situate-runtime-api`'s
 * authorization for `GET /v1/areas/{areaId}/map` (see
 * `docs/agent/implementation-plan/006-area-map-firestore-floorplans/README.md` in
 * that repository). Disabled until a runtime session client exists for the
 * deployment, the grant has been issued, and an area id is known; re-fetches
 * automatically whenever the runtime session or area id changes. This query's
 * own metadata result is not persisted beyond its TanStack Query lifetime -
 * its signed floor plan image URL expires after 5 minutes (the runtime API's
 * default) - but the underlying floorplan raster itself is fetched and cached
 * separately via `floorplanImageQueryOptions`, keyed by the stable
 * `deploymentId + floorplanId + revision` identity rather than this URL.
 *
 * Shares its query key with `useAreaMaps`'s fan-out preloading, so selecting
 * an already-preloaded area reuses that cache entry instead of re-fetching.
 */
export function useAreaMap(deploymentId: string, areaId: string | undefined, enabled = true) {
  const runtime = useRuntimeHttpClient();
  const hasAreasListGrant = runtime.operationGrants.some(
    (grant) => grant.operation_id === "areas_list"
  );
  const client = runtime.client;

  return useQuery<AreaMapResponse>({
    queryKey: areaMapQueryKey(deploymentId, areaId),
    queryFn: () => client!.get<AreaMapResponse>(buildAreaMapPath(areaId as string)),
    enabled:
      enabled &&
      Boolean(client) &&
      runtime.deploymentId === deploymentId &&
      hasAreasListGrant &&
      areaId != null,
  });
}
